import React, { useState } from 'react';
import {
  Divider,
  Typography,
  Card,
  Table,
  Tag,
  Button,
  Progress,
  Space,
  Modal,
  Empty,
  Tooltip
} from 'antd';
import {
  EmbeddedEstimatesTable,
  EmbeddedInvoicesTable,
} from '../../finance/EmbeddedFinanceDocs';
import {
  DollarCircleOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  EditOutlined,
  BankOutlined
} from '@ant-design/icons';
import { formatPriceToman } from '../../../utils/formatPriceToman';
import { toJalali } from '../../../utils/dateUtils';
import RegisterDealPaymentModal from '../RegisterDealPaymentModal';

const { Title, Text } = Typography;

const PAYMENT_METHODS = {
  card_to_card: 'کارت به کارت',
  bank_transfer: 'واریز به حساب / پایا',
  pos: 'کارتخوان (POS)',
  cheque: 'چک صیادی',
  gateway: 'درگاه پرداخت آنلاین',
  cash: 'نقدی',
};

const APPROVAL_STATUSES = {
  pending: { text: 'در انتظار تأیید', color: 'warning', icon: <ClockCircleOutlined /> },
  pending_approval: { text: 'در انتظار تأیید', color: 'warning', icon: <ClockCircleOutlined /> },
  needs_correction: { text: 'نیاز به اصلاح', color: 'orange', icon: <ClockCircleOutlined /> },
  approved: { text: 'تأیید شده', color: 'success', icon: <CheckCircleOutlined /> },
  rejected: { text: 'رد شده', color: 'error', icon: <CloseCircleOutlined /> },
};

/**
 * تب صورتحساب معامله — واریزی‌ها، پیش‌فاکتورها و فاکتورهای مرتبط
 */
const BillingTab = ({
  dealId,
  deal,
  clientId,
  data,
  onRefresh,
  navigateWithTrail,
  onOpenPaymentModal,
}) => {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [previewReceiptUrl, setPreviewReceiptUrl] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);

  const estimates = Array.isArray(data?.estimates) ? data.estimates : [];
  const invoices = Array.isArray(data?.invoices) ? data.invoices : [];
  const payments = Array.isArray(data?.payments) ? data.payments : [];

  const dealValue = Number(deal?.value || data?.financial_summary?.value || 0);
  const paidAmount = Number(deal?.paid_amount != null ? deal.paid_amount : (data?.financial_summary?.paid_amount || 0));
  const remainingAmount = Math.max(0, deal?.remaining_amount != null ? Number(deal.remaining_amount) : (dealValue - paidAmount));
  const settlementPercent = dealValue > 0 ? Math.min(100, Math.round((paidAmount / dealValue) * 100)) : 0;

  const handleOpenPayment = () => {
    if (onOpenPaymentModal) {
      onOpenPaymentModal();
    } else {
      setInternalModalOpen(true);
    }
  };

  const paymentColumns = [
    {
      title: 'شناسه',
      key: 'row_number',
      width: 80,
      render: (_, __, index) => <Text className="text-gray-400 text-xs">{index + 1}</Text>,
    },
    {
      title: 'مبلغ واریزی',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <span className="font-bold text-emerald-600 dark:text-emerald-400">
          {formatPriceToman(amount || 0)}
        </span>
      ),
    },
    {
      title: 'وضعیت تأیید',
      dataIndex: 'approval_status',
      key: 'approval_status',
      render: (status) => {
        const config = APPROVAL_STATUSES[status] || APPROVAL_STATUSES.pending_approval;
        return (
          <Tag color={config.color} className="flex items-center gap-1 w-fit rounded-md px-2 py-0.5 text-xs">
            {config.icon}
            <span>{config.text}</span>
          </Tag>
        );
      },
    },
    {
      title: 'روش پرداخت',
      dataIndex: 'payment_method_type',
      key: 'payment_method_type',
      render: (type) => (
        <span className="text-gray-600 dark:text-gray-300 text-xs">
          {PAYMENT_METHODS[type] || type || '—'}
        </span>
      ),
    },
    {
      title: 'حساب بانکی مقصد',
      key: 'bank_account',
      render: (_, record) => {
        const bank = record.bank_account;
        if (!bank) return <span className="text-gray-400 text-xs">—</span>;
        return (
          <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">
            {bank.bank_name || 'بانک'} {bank.account_number ? `(${bank.account_number})` : ''}
          </span>
        );
      },
    },
    {
      title: 'کد پیگیری',
      dataIndex: 'tracking_code',
      key: 'tracking_code',
      render: (code) => code ? (
        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
          {code}
        </span>
      ) : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      title: 'تاریخ پرداخت',
      key: 'paid_on',
      render: (_, record) => {
        const dateStr = record.paid_on_jalali || (record.paid_on ? toJalali(record.paid_on) : '—');
        return <span className="text-xs text-gray-500" dir="ltr">{dateStr}</span>;
      },
    },
    {
      title: 'ثبت‌کننده',
      key: 'sales_owner',
      render: (_, record) => {
        const owner = record.sales_owner;
        return (
          <span className="text-xs text-gray-500">
            {owner?.name || '—'}
          </span>
        );
      },
    },
    {
      title: 'فیش',
      key: 'receipt',
      width: 70,
      render: (_, record) => {
        if (!record.receipt_url) return <span className="text-gray-400 text-xs">ندارد</span>;
        return (
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => setPreviewReceiptUrl(record.receipt_url)}
            title="مشاهده تصویر فیش"
            className="text-blue-500"
          />
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const needsCorrection = record.approval_status === 'needs_correction';
        return (
          <Tooltip title={needsCorrection ? 'اصلاح و ارسال مجدد به مدیر مالی' : 'فقط واریزی‌های «نیاز به اصلاح» قابل ویرایش هستند'}>
            <Button
              size="small"
              type="text"
              disabled={!needsCorrection}
              icon={<EditOutlined />}
              onClick={() => setEditingPayment(record)}
              className={needsCorrection ? 'text-orange-500' : ''}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }} className="space-y-6">
      {/* 1. بخش واریزی‌ها و وضعیت مالی معامله */}
      <section className="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl border border-gray-200/80 dark:border-gray-700 shadow-sm">
        {/* هدر بخش واریزی */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center text-base">
              <DollarCircleOutlined />
            </div>
            <div>
              <Title level={5} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
                واریزی‌ها و وضعیت تسویه معامله
              </Title>
              <Text className="text-gray-500 dark:text-gray-400 text-xs">
                مدیریت پرداخت‌های ثبت‌شده توسط کارشناس و پیگیری وضعیت تأیید مدیر مالی
              </Text>
            </div>
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenPayment}
            className="bg-emerald-600 hover:bg-emerald-500 font-medium rounded-lg text-xs sm:text-sm flex items-center gap-1.5 shadow-sm"
          >
            ثبت واریزی معامله
          </Button>
        </div>

        {/* کارت‌های خلاصه وضعیت مالی معامله */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 bg-gray-50/70 dark:bg-gray-900/40 rounded-xl mb-5 border border-gray-100 dark:border-gray-800">
          <div>
            <span className="text-gray-500 text-xs block mb-1">ارزش کل معامله</span>
            <span className="font-bold text-sm sm:text-base text-gray-800 dark:text-gray-200">
              {formatPriceToman(dealValue)}
            </span>
          </div>
          <div>
            <span className="text-gray-500 text-xs block mb-1">کل وصولی تأییدشده</span>
            <span className="font-bold text-sm sm:text-base text-emerald-600">
              {formatPriceToman(paidAmount)}
            </span>
          </div>
          <div>
            <span className="text-gray-500 text-xs block mb-1">مانده قابل پرداخت</span>
            <span className="font-bold text-sm sm:text-base text-amber-600">
              {formatPriceToman(remainingAmount)}
            </span>
          </div>
          <div>
            <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
              <span>درصد تسویه</span>
              <span className="font-bold text-gray-700 dark:text-gray-300">{settlementPercent}%</span>
            </div>
            <Progress
              percent={settlementPercent}
              size="small"
              status={settlementPercent >= 100 ? 'success' : 'active'}
              showInfo={false}
              strokeColor="#10b981"
            />
          </div>
        </div>

        {/* جدول واریزی‌ها */}
        {payments.length === 0 ? (
          <div className="py-6 text-center bg-gray-50/50 dark:bg-gray-800/30 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className="text-gray-400 text-xs">
                  هنوز هیچ واریزی برای این معامله ثبت نشده است.
                </span>
              }
            >
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleOpenPayment}
                className="text-emerald-600 border-emerald-400 hover:text-emerald-500 rounded-lg text-xs"
              >
                ثبت اولین واریزی
              </Button>
            </Empty>
          </div>
        ) : (
          <Table
            dataSource={payments}
            columns={paymentColumns}
            rowKey="id"
            pagination={false}
            size="small"
            className="custom-table"
          />
        )}
      </section>

      <Divider className="my-2" />

      {/* 2. پیش‌فاکتورها */}
      <section>
        <Title level={5} style={{ fontFamily: 'CustomFont', marginBottom: 12 }}>
          پیش‌فاکتورها
        </Title>
        <EmbeddedEstimatesTable
          estimates={estimates}
          showDealColumn={false}
          showProjectColumn
          clientId={clientId}
          dealId={dealId}
          onChanged={onRefresh}
          createEnabled
          navigateWithTrail={navigateWithTrail}
        />
      </section>

      <Divider className="my-2" />

      {/* 3. فاکتورها */}
      <section>
        <Title level={5} style={{ fontFamily: 'CustomFont', marginBottom: 12 }}>
          فاکتورها
        </Title>
        <EmbeddedInvoicesTable
          invoices={invoices}
          showDealColumn={false}
          showProjectColumn
          clientId={clientId}
          dealId={dealId}
          onChanged={onRefresh}
          createEnabled
          navigateWithTrail={navigateWithTrail}
        />
      </section>

      {/* Internal Payment Modal if not managed by parent */}
      {!onOpenPaymentModal && (
        <RegisterDealPaymentModal
          open={internalModalOpen}
          onClose={() => setInternalModalOpen(false)}
          deal={{
            id: dealId,
            name: deal?.name,
            value: dealValue,
            paid_amount: paidAmount,
            remaining_amount: remainingAmount,
            client: deal?.client,
          }}
          onSuccess={onRefresh}
        />
      )}

      {/* مودال اصلاح واریزی «نیاز به اصلاح» */}
      <RegisterDealPaymentModal
        open={Boolean(editingPayment)}
        onClose={() => setEditingPayment(null)}
        editPayment={editingPayment}
        deal={{
          id: dealId,
          name: deal?.name,
          value: dealValue,
          paid_amount: paidAmount,
          remaining_amount: remainingAmount,
          client: deal?.client,
        }}
        onSuccess={onRefresh}
      />

      {/* Preview Receipt Modal */}
      <Modal
        open={Boolean(previewReceiptUrl)}
        footer={null}
        onCancel={() => setPreviewReceiptUrl(null)}
        centered
        width={600}
      >
        {previewReceiptUrl && (
          <img
            src={previewReceiptUrl}
            alt="فیش واریزی"
            style={{ width: '100%', borderRadius: 8, maxHeight: '80vh', objectFit: 'contain' }}
          />
        )}
      </Modal>
    </div>
  );
};

export default BillingTab;
